import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Newspaper, Bookmark, Shield, LogOut, LogIn, TrendingUp, User, Mail, Trophy } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import NewsletterModal from './NewsletterModal';
import LanguageSwitcher from './LanguageSwitcher';
import PointsWidget from './PointsWidget';

const Header = () => {
  const { user, isAdmin, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [showSubscribe, setShowSubscribe] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
    <header className="bg-[#FFFDF5] dark:bg-[#1a1816] border-b-2 border-black dark:border-white sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
            <div className="bg-[#FF6B6B] p-1.5 sm:p-2 rounded-xl border-2 border-black dark:border-white hard-shadow-hover">
              <Newspaper className="w-5 h-5 sm:w-6 sm:h-6 text-white" strokeWidth={2.5} />
            </div>
            <h1 className="text-xl sm:text-3xl font-bold tracking-tight dark:text-white" style={{ fontFamily: 'Merriweather, serif' }}>
              Daily Wit
            </h1>
          </Link>

          <nav className="flex items-center gap-2 sm:gap-4">
            {/* My Story Link */}
            <Link
              to="/my-story"
              data-testid="my-story-link"
              className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full border-2 border-black dark:border-white font-bold transition-all text-sm sm:text-base ${
                location.pathname === '/my-story'
                  ? 'bg-[#FF6B6B] text-white hard-shadow'
                  : 'bg-white dark:bg-[#2D2A26] dark:text-white hover:bg-[#F0F0F0] dark:hover:bg-[#3D3A36] hard-shadow-hover'
              }`}
            >
              <User className="w-4 h-4" strokeWidth={2.5} />
              <span className="hidden sm:inline">{t('nav.myStory')}</span>
            </Link>

            {/* Subscribe Button */}
            <button
              onClick={() => setShowSubscribe(true)}
              data-testid="subscribe-header-btn"
              className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full border-2 border-black dark:border-white font-bold transition-all text-sm sm:text-base bg-[#4ECDC4] text-black hover:bg-[#3DBDB5] hard-shadow-hover"
            >
              <Mail className="w-4 h-4" strokeWidth={2.5} />
              <span className="hidden sm:inline">{t('nav.subscribe')}</span>
            </button>

            {/* Leaderboard Link */}
            <Link
              to="/leaderboard"
              data-testid="leaderboard-link"
              className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full border-2 border-black dark:border-white font-bold transition-all text-sm sm:text-base ${
                location.pathname === '/leaderboard'
                  ? 'bg-[#FFE66D] text-black hard-shadow'
                  : 'bg-white dark:bg-[#2D2A26] dark:text-white hover:bg-[#F0F0F0] dark:hover:bg-[#3D3A36] hard-shadow-hover'
              }`}
            >
              <Trophy className="w-4 h-4" strokeWidth={2.5} />
              <span className="hidden sm:inline">{t('nav.leaderboard')}</span>
            </Link>

            <Link
              to="/trending"
              data-testid="trending-link"
              className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full border-2 border-black dark:border-white font-bold transition-all text-sm sm:text-base ${
                location.pathname === '/trending'
                  ? 'bg-[#FF6B6B] text-white hard-shadow'
                  : 'bg-white dark:bg-[#2D2A26] dark:text-white hover:bg-[#F0F0F0] dark:hover:bg-[#3D3A36] hard-shadow-hover'
              }`}
            >
              <TrendingUp className="w-4 h-4" strokeWidth={2.5} />
              <span className="hidden sm:inline">{t('nav.trending')}</span>
            </Link>
            {user ? (
              <>
                <PointsWidget />
                <Link
                  to="/bookmarks"
                  data-testid="bookmarks-link"
                  className={`hidden sm:flex items-center gap-2 px-4 py-2 rounded-full border-2 border-black dark:border-white font-bold transition-all ${
                    location.pathname === '/bookmarks'
                      ? 'bg-[#4ECDC4] text-black hard-shadow'
                      : 'bg-white dark:bg-[#2D2A26] dark:text-white hover:bg-[#F0F0F0] dark:hover:bg-[#3D3A36] hard-shadow-hover'
                  }`}
                >
                  <Bookmark className="w-4 h-4" strokeWidth={2.5} />
                  <span className="hidden sm:inline">{t('nav.bookmarks')}</span>
                </Link>
                {isAdmin && (
                <Link
                  to="/admin"
                  data-testid="admin-link"
                  className={`hidden sm:flex items-center gap-2 px-4 py-2 rounded-full border-2 border-black dark:border-white font-bold transition-all ${
                    location.pathname === '/admin'
                      ? 'bg-[#FFE66D] text-black hard-shadow'
                      : 'bg-white dark:bg-[#2D2A26] dark:text-white hover:bg-[#F0F0F0] dark:hover:bg-[#3D3A36] hard-shadow-hover'
                  }`}
                >
                  <Shield className="w-4 h-4" strokeWidth={2.5} />
                  <span className="hidden sm:inline">{t('nav.admin')}</span>
                </Link>
                )}
                <button
                  onClick={handleLogout}
                  data-testid="logout-button"
                  className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full border-2 border-black dark:border-white bg-transparent dark:text-white font-bold hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-all hard-shadow-hover text-sm sm:text-base"
                >
                  <LogOut className="w-4 h-4" strokeWidth={2.5} />
                  <span className="hidden sm:inline">{t('nav.logout')}</span>
                </button>
              </>
            ) : (
              <Link
                to="/auth"
                data-testid="login-link"
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-1.5 sm:py-3 rounded-full border-2 border-black dark:border-white bg-[#FF6B6B] text-white font-bold hover:bg-[#FF5252] transition-all hard-shadow-hover text-sm sm:text-base"
              >
                <LogIn className="w-4 h-4" strokeWidth={2.5} />
                <span className="hidden sm:inline">{t('nav.login')}</span>
              </Link>
            )}
            <LanguageSwitcher />
            <ThemeToggle />
          </nav>
        </div>
      </div>
    </header>
    
    {/* Newsletter Subscribe Modal */}
    <NewsletterModal isOpen={showSubscribe} onClose={() => setShowSubscribe(false)} />
    </>
  );
};

export default Header;